import React from 'react'
import { graphql } from 'gatsby'
import { UmbracoPage } from '@mod/umbraco'
import { Latest } from '@mod/features/src/DashboardTabs'

import { template } from '@mod/components/utils'
import { useMyInfo } from '@mod/api'
import { DashboardSettings } from '@mod/features/src/DashboardTabs/DashboardTabs.types'
import { Layout } from '../layout'

const Home: UmbracoPage = ({ location, navigate, data, pageContext }) => {
  const { dashSettings, latestFeatures } = data.umbraco.content as {
    dashSettings?: DashboardSettings
    latestFeatures?: any
  }

  const [, myInfo] = useMyInfo()
  const { serviceName } = myInfo

  // Latest Tab
  const serviceHeaderLatest =
    dashSettings.latestHeaders &&
    dashSettings.latestHeaders.find(element => element.service === serviceName)

  // Latest Tab - Background
  const hasLatestBg = serviceHeaderLatest && serviceHeaderLatest.backgroundImage
  const latestHero = hasLatestBg
    ? serviceHeaderLatest.backgroundImage.fileNode.childImageSharp.fluid
    : dashSettings.defaultLatestBackgroundImage.fileNode.childImageSharp.fluid

  // Latest Tab - Badge
  const hasBadge = serviceHeaderLatest && serviceHeaderLatest.badgeImage
  const badge = hasBadge ? serviceHeaderLatest.badgeImage.fileNode.childImageSharp.fixed : null

  // Latest Tab - Latest Message
  const hasLatestMessage =
    serviceHeaderLatest &&
    serviceHeaderLatest.latestMessage &&
    serviceHeaderLatest.latestMessage !== ''

  const latestMessage = hasLatestMessage
    ? template(serviceHeaderLatest.latestMessage, myInfo)
    : template(dashSettings.defaultLatestMessage, myInfo)

  // Latest Tab - CTA
  const hasCta =
    serviceHeaderLatest && serviceHeaderLatest.ctaText && serviceHeaderLatest.ctaText !== ''

  const cta = hasCta
    ? {
        ctaText: serviceHeaderLatest.ctaIntroduction,
        ctaLinkText: serviceHeaderLatest.ctaText,
        ctaLink: serviceHeaderLatest.ctaUrl || '/',
      }
    : {
        ctaText: dashSettings.defaultLatestCTAIntroduction,
        ctaLinkText: dashSettings.defaultLatestCTAText,
        ctaLink: dashSettings.defaultLatestCTAUrl || '/',
      }

  // Always show the CTA regardless of announcements
  const alwaysShowCta = serviceHeaderLatest
    ? serviceHeaderLatest.ctaAlwaysDisplay
    : dashSettings.defaultLatestCTAAlwaysDisplay || false

  // Latest Tab - Dismissed message

  const hasDismissed =
    serviceHeaderLatest &&
    serviceHeaderLatest.dismissedMessage &&
    serviceHeaderLatest.dismissedMessage !== ''

  const dismissedMessage = hasDismissed
    ? {
        title: template(serviceHeaderLatest.dismissedMessage, myInfo),
        intro: serviceHeaderLatest.introduction,
      }
    : {
        title: template(dashSettings.defaultLatestDismissedMessage, myInfo),
        intro: dashSettings.defaultLatestIntroduction,
      }

  const showCtaAsBanner = serviceName === 'RFA'

  const latestFeaturesSlides = latestFeatures._contentData.children.items.map(feature => {
    const featureCta = feature.cta && feature.cta.url.replace(/\/home-my-(navy|raf)/gi, '')
    const { id } = feature._contentData
    const image = feature.image && feature.image.fileNode.childImageSharp.fluid
    const video = feature.video && feature.video.umbracoFile

    return { ...feature, cta: featureCta, id, image, video }
  })

  const resetTab = () => {
    return true
  }

  return (
    <>
      <Layout
        resetTab={resetTab}
        title="Dashboard"
        location={location}
        navigate={navigate}
        isDashboard
      >
        <Latest
          site={pageContext.site}
          background={latestHero}
          dismissedMessage={dismissedMessage}
          latestMessage={latestMessage}
          badge={badge}
          cta={cta}
          alwaysShowCta={alwaysShowCta}
          showCtaAsBanner={showCtaAsBanner}
          latestFeatures={latestFeaturesSlides}
        />
      </Layout>
    </>
  )
}

export const query = graphql`
  {
    umbraco {
      content {
        latestFeatures: byUrl(url: "/home-my-navy/settings/latest-features/") {
          ... on UMBRACO_LatestFeaturesFolder {
            _contentData {
              children(filter: { index_lte: 2 }) {
                items {
                  ... on UMBRACO_LatestFeature {
                    _contentData {
                      id
                    }
                    overlayTitle
                    title
                    summary
                    image {
                      ... on UMBRACO_Image {
                        umbracoFile
                        fileNode {
                          childImageSharp {
                            fluid(maxWidth: 500, quality: 100) {
                              ...GatsbyImageSharpFluid
                            }
                          }
                        }
                      }
                    }
                    video {
                      ... on UMBRACO_File {
                        umbracoFile
                      }
                    }
                    cta {
                      url
                    }
                  }
                }
              }
            }
          }
        }
        dashSettings: byUrl(url: "/home-my-navy/settings/dashboard-settings") {
          ... on UMBRACO_DashboardSettings {
            defaultLatestBackgroundImage {
              ... on UMBRACO_Image {
                umbracoFile
                fileNode {
                  childImageSharp {
                    fluid(maxWidth: 1439, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
            defaultLatestDismissedMessage
            defaultLatestMessage
            defaultLatestIntroduction
            defaultLatestCTAUrl
            defaultLatestCTAText
            defaultLatestCTAIntroduction
            defaultLatestCTAAlwaysDisplay
            latestHeaders {
              ... on UMBRACO_LatestTabSettings {
                service
                title
                dismissedMessage
                latestMessage
                introduction
                ctaUrl
                ctaText
                ctaIntroduction
                ctaAlwaysDisplay
                backgroundImage {
                  ... on UMBRACO_Image {
                    umbracoFile
                    fileNode {
                      childImageSharp {
                        fluid(maxWidth: 1439, quality: 100) {
                          ...GatsbyImageSharpFluid
                        }
                      }
                    }
                  }
                }
                badgeImage {
                  ... on UMBRACO_Image {
                    umbracoFile
                    fileNode {
                      childImageSharp {
                        fixed(width: 150, quality: 100) {
                          ...GatsbyImageSharpFixed
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export default Home
